import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { parseISO } from 'date-fns';

const bookingTypes = [
  { label: "1:1 calls", value: "1on1" },
  { label: "Package/Subscriptions", value: "package" },
  { label: "Webinar", value: "webinar" },
  { label: "Digital Products", value: "digital" },
];

const sections = ["Upcoming", "Live", "Completed"];

const ExpertBookings = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState("1on1");
  const [activeSection, setActiveSection] = useState("Upcoming");
  const [bookings, setBookings] = useState([]);

  const fetchBookings = useCallback(async () => {
    const statusParam = activeSection === 'Completed' ? 'completed' : 'upcoming';
    try {
      const res = await axios.get(`https://quirkleai.com/api/bookings/?type=${activeType}&status=${statusParam}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const now = new Date();
      if (activeSection === 'Live') {
        const liveSessions = res.data.filter(b => {
          const startTime = parseISO(b.start_time);
          const endTime = parseISO(b.end_time);
          return now >= startTime && now <= endTime;
        });
        setBookings(liveSessions);
      } else if (activeSection === 'Upcoming') {
        const upcomingSessions = res.data.filter(b => {
          const startTime = parseISO(b.start_time);
          return now < startTime;
        });
        setBookings(upcomingSessions);
      } else { // Completed
        setBookings(res.data);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings.");
    }
  }, [activeType, activeSection, accessToken]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleStartNow = async (sessionId, meetingId) => {
    try {
      await axios.patch(`https://quirkleai.com/api/sessions/${sessionId}/activate/`, {}, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success("Meeting activated!");
      navigate(`/therapist/jitsi-room/${meetingId}`);
    } catch (error) {
      console.error("Error activating meeting:", error);
      toast.error("Failed to activate meeting.");
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Bookings</h1>

      <div className="flex flex-wrap gap-3">
        {bookingTypes.map(({ label, value }) => (
          <Button
            key={label}
            variant={activeType === value ? 'default' : 'outline'}
            onClick={() => setActiveType(value)}
          >
            {label}
          </Button>
        ))}
      </div>

      <div className="flex gap-4 border-b text-sm font-medium text-gray-600 mt-4">
        {sections.map((section) => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            className={`pb-2 border-b-2 ${
              activeSection === section
                ? 'border-black text-black'
                : 'border-transparent hover:text-black'
            }`}
          >
            {section}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {bookings.map((b) => (
          <div key={b.id} className="p-3 border rounded bg-white shadow-sm">
            With: {b.client.username} on {new Date(b.start_time).toLocaleString()}
            {activeSection === "Live" && (
              <Button
                className="ml-4"
                onClick={() => handleStartNow(b.id, b.meeting_id)}
              >
                Start Now
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="bg-yellow-50 text-center py-8 px-4 rounded-md border border-dashed border-yellow-300">
        <img
          src="/static/share-illustration.png"
          alt="Share"
          className="mx-auto mb-4"
          style={{ maxWidth: '140px' }}
        />
        <h2 className="text-lg font-semibold">Share your page</h2>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          A new booking might just be around the corner. Share your page today!
        </p>
        <Button className="mt-4 px-6">Share page</Button>
      </div>
    </div>
  );
};

export default ExpertBookings;